import { NumberOrNull, StringOrNull } from 'store/entity/types';
import {
  EndTime,
  IProviderItemView,
  ITags,
  ITagView,
  ITaskReportInfo,
  ReportViewType,
} from 'store/providers/providerTypes';
import { IDocumentView } from 'store/companies/companiesTypes';
import { removeDuplicates } from '../../utils/happyFunctions';

export const SURVEY_ANALYSE_LINK_DESC_NAME = 'analyse';
export const SurveyAnalyseTemplateType = 'SurveyAnalyse';

export type ScoreMethodType = 'sum' | 'totalAverage';

export type VisibilityStrategy = 'personal' | 'protected' | 'open';

export type NotificationMedium = 'SMS' | 'Slack' | 'Hook';

export type TimeScale =
  | 'day'
  | 'week'
  | 'month'
  | 'season'
  | 'twoSeason'
  | 'year';

export type PeriodType =
  | 'none'
  | 'day'
  | 'week'
  | 'month'
  | 'season'
  | 'twoSeason'
  | 'year';

export type Metric =
  | 'avg'
  | 'count'
  | 'percentageOfCount'
  | 'totalAvg'
  | 'sum'
  | 'percentageOfAvg'
  | 'percentageOfTotalAvg'
  | 'responseList';

export type SeriesType = 'all' | 'tag' | 'question' | 'list';

export type ChartTypes =
  | 'line'
  | 'bar'
  | 'table'
  | 'tableRev'
  | 'barRev'
  | 'stack'
  | 'stackRev'
  | 'pie'
  | 'iranMap'
  | null;

export type SurveyAction =
  | 'Submit'
  | 'Update'
  | 'Lock'
  | 'Unlock'
  | 'DraftSubmit'
  | 'Draft'
  | 'Undraft'
  | 'UpdateByAdmin'
  | 'AnswerChanged'
  | 'Scheduled';

export interface UploadProgressState {
  byQuestion: Record<string, number>;
}

type Field = { name: string; value: string };

type ReportFieldType = 'question' | 'tag' | 'user' | 'instance' | 'score';

export type InstanceType = 'Provider' | 'ProviderItem' | 'User';

interface IChart {
  name: string;
  urlParam: { survey_token: string };
  chartType: string;
  pathToQueryData: string;
  domainToQueryData: string;
}

export interface ISurveyItem extends IProviderItemView {
  changeDataToken: string;
  chart: IChart;
  editToken: string;
  fillToken: string;
  instance: number;
  instanceType: InstanceType;
  targetRolesIds?: Array<number>;
  name: string;
  removable: boolean;
  reportToken: string;
  surveyId: number;
  surveyName: string;
  myResponseInfo: {
    filledPeriods: Array<string>;
  };
  extraOwnerTokens?: string[];
  ignoreInstance?: boolean;
}

export interface ISurveyItemWithData {
  data: ISurveyItem;
}
export type SeriesOrder = 'Default' | 'ValueAsc' | 'ValueDesc';
export interface ISeries {
  metric: Metric;
  type: SeriesType;
  name?: string;
  color?: string;
  tagId?: NumberOrNull;
  tagIds?: Array<number>;
  split?: boolean;
  questionId?: string | null;
  subQuestionId?: StringOrNull;
  splitOnValues?: boolean;
  includeTotal?: boolean;
  valueFilter?: string[];
  splitOnFieldValues?: string;
  customId?: string;
  questionIds?: Array<number>;
  chartType: ChartTypes;
  order?: SeriesOrder;
}

export interface IReportField {
  includeTotal: boolean;
  name?: string | null;
  split: boolean;
  hideMissingData?: boolean;
  type: ReportFieldType;
  depth?: number;
}

export interface IQuestionReportField extends IReportField {
  type: 'question';
  questionId: number;
  subQuestionId: StringOrNull;
  choiceIds: Array<string>;
  depth?: number;
}

export interface ITagValues {
  tag: number;
  child: boolean;
}

export interface ITagReportField extends IReportField {
  type: 'tag';
  values: Array<ITagValues>;
}

export interface IInstanceReportField extends IReportField {
  type: 'instance';
  values: Array<number>;
  instanceType: InstanceType | null;
}

export interface IScoreRangeReportField extends IReportField {
  type: 'score';
  min?: number | null;
  max?: number | null;
  questionId?: number | null;
}

export interface ITimeInfo {
  timeScale: TimeScale;
  timeLength: number;
  splitOnTime: boolean;
  totalTime: boolean;
  similarPastYearPeriodLength: number | undefined;
  offsetPeriod?: number | undefined;
}

export interface DashboardLink {
  name: string;
  url: string;
  icon: string;
}

export interface IReportView {
  time: ITimeInfo;
  fields: Array<IReportField>;
  series: Array<ISeries>;
  surveyId?: number;
  ownerId?: number;
  name: string;
  id: number;
  order?: number;
  tags: Array<string>;
  dashboardWidth?: number;
  dashboardHeight?: number;
  dashboardLinks: Array<DashboardLink>;
  type?: string;
  surveyToken?: string; // for client use only
  flag?: string | null | undefined;
  viewOnlyInMainOwner?: boolean;
  colorMap?: Record<string, string>;
}

export interface ICheckPoint {
  id?: number;
  surveyId: NumberOrNull;
  reportId: NumberOrNull;
  date: NumberOrNull;
  baseLine: NumberOrNull;
  target: NumberOrNull;
  properties?: {
    distinctiveValues?: {
      [key: string]: { baseLine?: number; target?: number };
    };
  };
}

interface ICheckPoints {
  data: { [key: number]: ICheckPoint };
  status: string;
}

export interface CumulativePercentage {
  id: string;
  avg: number;
  boundAvg: number;
  items: { [key: number]: CumulativePercentageItem };
}

export interface CumulativePercentageItem {
  key: string;
  itemAvg: number;
  itemBoundAvg: number;
}
export interface ISurveyReportMap {
  [key: number]: IReportView;
}
export interface ISurveyReports {
  data: ISurveyReportMap;
  loaded: boolean;
}

interface ISurveyTarget {
  date: number;
  base: number;
  target: number;
}

export interface INotifGroup {
  groupId: string;
  groupTitle: string;
  userIds: number[];
  roleIds: number[];
}

export interface SurveyNotificationMessage {
  userId: number;
  message: string;
  medium: NotificationMedium;
}

export interface ISurveyView extends ISurveyPartialProperties {
  id?: number;
  maxScore?: NumberOrNull;
  responseMap?: {
    [key: string]: ISurveyResponseView;
  };
  surveyTargets?: ISurveyTarget[] | null;
  unit?: NumberOrNull;
  currentVersion: string;
  versionTitle: string;
  versionDescription: string;
  visibilityStrategy?: VisibilityStrategy;
  showResponseToTarget: boolean;
  questionMap?: {
    [questionId: number]: IQuestionsViewWithData;
  };
  notifications: ISurveyNotification[];
  notifGroups: INotifGroup[];
  allowEditNotifGroupMembersTo: number[];
  stageQuestion?: number;
  versions: { [versionId: string]: ISurveyProperties };
  lastSortingIndex?: number;
  validListSize?: number;
  fillValidPeriod: {
    fromOffsetDaysRelativeToStartOfPeriod: number | null;
    toOffsetDaysRelativeToEndOfPeriod: number | null;
  };
  restrictionOfCompletion?: boolean;
}

export interface ISurveyViewWithData {
  data: ISurveyView;
  isValid: boolean;
  isLoading: boolean;
  allResponsesLoaded?: boolean;
  err?: any;
  viewingVersion?: string;
  reports?: ISurveyReports;
}

export interface ISurveyPropertiesDB extends ISurveyPartialProperties {
  reportIds?: [number];
}

export interface ISurveyViewServer {
  askTitle: boolean;
  offerToFillAtFirst: boolean;
  autoLock: boolean;
  description: string;
  fields: Array<Field>;
  id: number;
  keepUnique: boolean;
  maxScore: NumberOrNull;
  name: string;
  periodType: PeriodType;
  surveyQuestionList: Array<IQuestionsView>;
  recommendRefill: boolean;
  surveyTargets: Array<ISurveyTarget> | null;
  surveyToken?: string;
  uniquePerInstance: boolean;
  uniquePerTime: boolean;
  uniquePerUser: boolean;
  keepResponsesAnonymous: boolean;
  keepLatestInstanceIfAnonymous: boolean;
  currentVersion: string;
  versionTitle: string;
  versionDescription: string;
  visibilityStrategy?: VisibilityStrategy;
  showResponseToTarget: boolean;
  unit: NumberOrNull;
  versions: Array<ISurveyProperties>;
  notifications: Array<ISurveyNotification>;
  notifGroups: Array<INotifGroup>;
  allowEditNotifGroupMembersTo: Array<number>;
}

export interface ISurveyResponseChangeLog {
  effectiveDate?: number;
  title?: number;
  answerList: Array<IAnswersView>;
  score?: number;
  scoreSum?: number;
  scoreCount?: number;
  userId?: number;
  updateDate?: number;
}
export interface ISurveyResponseView {
  id?: number;
  tags?: Array<number>;
  lock?: boolean;
  draft?: boolean;
  title?: StringOrNull;
  userId?: number;
  surveyId: number;
  instance?: number;
  submitDate?: number;
  updateDate?: number;
  lastUpdateBy?: number;
  effectiveDate?: number;
  surveyAnswerList: Array<IAnswersView>;
  changeLog?: Array<ISurveyResponseChangeLog>;
  score?: number;
  scoreSum?: number;
  scoreCount?: number;
  surveyVersion: string;
  extraInfo?: {
    itemId?: number;
    providerId?: number;
  };
  deleted?: boolean;
  activeQuestions?: Array<number>;
  extraOwnerTokens?: string[];
}

export interface ISurveyInfo {
  fillToken: string;
  editToken: string;
  name: string;
  id: number;
}

export interface IAnswerView {
  questionId: number;
  description?: string;
  error?: any;
  value?: any;
  score?: number;
}

export interface ISubAnswer {
  value: string;
  subQuestionValue: string;
  description?: string;
}

export interface ISurveys {
  data: { [key: number]: ISurveyViewWithData };
  reports: ISurveyReports;
  checkPoints: { data: ICheckPoints; loaded: boolean };
  cumulativePercentage: { [key: number]: CumulativePercentage };
  uploadProgress: UploadProgressState;
}

// ------------------------------- Questions

export interface ICommonQuestionView {
  id: number;
  hint: string;
  tags: ITagView[];
  title: string;
  order: number;
  weight: number;
  fields?: IQuestionField[];
  unifier: boolean;
  deleted: boolean;
  disabled: boolean;
  hintAdded: boolean;
  distinctive: boolean;
  showInTitle: boolean;
  answerRequired: boolean;
  viewCondition?: IViewConditionView;
  protection?: QuestionProtection;
  viewableWith?: string[];
  editableWith?: string[];
  indexOrder?: number;
  goDeepInTags?: number;
  levelTitles?: string[];
  hidePathValues?: boolean;
  modificationDesc?: string;
  modificationType?: 'deleted' | 'created' | 'edited' | 'preserved';
}

export interface IQuestionsViewWithData {
  data: IQuestionsView;
  state: QuestionState;
  err?: any;
}

export interface ITextQuestionView extends ICommonQuestionView {
  type: 'text';
  maxLength: number;
  inputType:
    | 'text'
    | 'number'
    | 'phone'
    | 'date'
    | 'dateAndTime'
    | 'duration'
    | 'digits'
    | 'area'
    | 'inid'
    | 'map'
    | 'rich'
    | 'none';
}

export interface ITextAnswerView extends IAnswerView {
  type: 'text';
  value: string;
}

export interface IFileQuestionView extends ICommonQuestionView {
  type: 'file';
  maxSizeInKB: NumberOrNull;
  validFormat: Array<FileTypes> | null;
}

export interface IFileAnswerView extends IAnswerView {
  type: 'file';
  file: IDocumentView;
  justUploaded?: boolean;
}

export interface IQuestionField {
  name: string;
  options: string[] | null;
  value: string;
  valueList: string[] | null;
}

export interface IChoiceView {
  text: string;
  score: number | null;
  value: string;
}

export interface IViewConditionView {
  refQuestionId: number;
  questionValues: string[];
}

export type QuestionProtection = 'Edit' | 'View';

export interface IGridQuestionView extends ICommonQuestionView {
  type: 'grid';
  parts: IChoiceView[];
  choiceList: IChoiceView[];
  mergeSumAndCount: boolean;
  unifyValueAndText: boolean;
}

export interface IGridAnswerView extends IAnswerView {
  type: 'grid';
  scoreSum: number;
  scoreCount: number;
  subAnswers: Array<ISubAnswer>;
}

export interface IIndicatorQuestionView extends ICommonQuestionView {
  type: 'indicator';
  unit: StringOrNull;
  numerator: string;
  denominator: StringOrNull;
  numeratorUnit: StringOrNull;
  denominatorUnit: StringOrNull;
}

export interface IIndicatorAnswerView extends IAnswerView {
  type: 'indicator';
  numerator: number;
  denominator: number;
}

export interface IMultipleChoiceQuestionView extends ICommonQuestionView {
  type: 'multipleChoice';
  useTags: boolean;
  viewMode: 'list' | 'popup';
  choiceList: IChoiceView[];
  tagChoices?: number[];
  multiSelect: boolean;
  distinctive: boolean;
  parentTagChoices?: number[];
  excludedTagChoices: number[];
  defaultValue: any[];
  fillPermissionSchemeList: number[] | undefined;
  reportPermissionSchemeList: number[] | undefined;
  unifyValueAndText: boolean;
  deleted: boolean;
  readFromItemTag: boolean;
}

export interface IMultipleChoiceAnswerView extends IAnswerView {
  type: 'multipleChoice';
  value: string;
  values: string[];
  selected: number[];
  error?: { emptyChoices: string };
}

export interface IViewConditionQuestionField {
  refQuestionId?: number;
  questionValues?: string[];
  refQuestionPart?: string;
}

export interface IMultipleAnswerField {
  key?: StringOrNull;
  title: StringOrNull;
  popup?: boolean;
  hidden?: boolean;
  options: string[] | null;
  flags: string[] | null;
  required?: boolean;
  withValue?: boolean;
  inputType?: IMultipleAnswerQuestionFieldInputType;
  protection?: QuestionProtection;
  valueMap?: Record<string, number>;
  viewCondition?: IViewConditionQuestionField;
}

export type IMultipleAnswerQuestionFieldInputType =
  | 'text'
  | 'number'
  | 'duration'
  | 'date'
  | 'time';

export interface IAnswerField {
  key: StringOrNull;
  value: StringOrNull;
}

export interface IMultipleAnswerSubAnswer {
  ref: string;
  order: number;
  values: IAnswerField[] | null;
  implicit: boolean;
}

export interface IMultipleAnswerQuestionView extends ICommonQuestionView {
  type: 'multipleAnswer';
  maxAnswers?: number;
  questionFields: Array<IMultipleAnswerField>;
}

export interface IMultipleAnswerAnswerView extends IAnswerView {
  type: 'multipleAnswer';
  subAnswerList: IMultipleAnswerSubAnswer[];
}

export type IQuestionsView =
  | ITextQuestionView
  | IFileQuestionView
  | IGridQuestionView
  | IIndicatorQuestionView
  | IMultipleChoiceQuestionView
  | IMultipleAnswerQuestionView;

export type IAnswersView =
  | ITextAnswerView
  | IFileAnswerView
  | IGridAnswerView
  | IIndicatorAnswerView
  | IMultipleChoiceAnswerView
  | IMultipleAnswerAnswerView;

export interface IFormValues {
  data: IQuestionsView;
  state: QuestionState;
}

export type QuestionTypes =
  | 'text'
  | 'file'
  | 'grid'
  | 'indicator'
  | 'multipleChoice'
  | 'multipleAnswer';

export type FileTypes =
  | 'audio'
  | 'video'
  | 'image'
  | 'document'
  | 'compressed'
  | 'pdf'
  | 'office';

export interface IQuestionTags {
  [tagId: string]: ITagView;
}

export type QuestionState =
  | 'exists'
  | 'editing'
  | 'removed'
  | 'creating'
  | 'uploading';

export function getChoices(
  question: IMultipleChoiceQuestionView,
  tags: ITags['data'] | undefined
) {
  let choices: IMultipleChoiceQuestionView['choiceList'] = [];
  if (question.useTags && tags) {
    if (question.parentTagChoices) {
      question.parentTagChoices.forEach((parentId) => {
        Object.values(tags)
          .filter(
            (tag) =>
              tag.id &&
              tag.parentId === parentId &&
              !question.excludedTagChoices?.includes(Number(tag.id))
          )
          .forEach((item) =>
            choices.push({
              text: item.text,
              value: item.id.toString(),
              score: null,
            })
          );
      });
    }
    if (question.tagChoices) {
      question.tagChoices.forEach((tagChoice: number) => {
        choices.push({
          text: tags[tagChoice].text,
          value: tagChoice.toString(),
          score: null,
        });
      });
    }
    // number[]
    choices = removeDuplicates(choices);
  } else {
    choices = question.choiceList;
  }
  return choices;
}

export const QuestionType: {
  [name: string]: QuestionTypes;
} = {
  Text: 'text',
  MultipleChoice: 'multipleChoice',
  Grid: 'grid',
  File: 'file',
  Indicator: 'indicator',
  MultipleAnswer: 'multipleAnswer',
};

export class Question {
  id: number;
  hint: string;
  order: number;
  title: string;
  answerRequired: boolean;

  constructor(
    id: number,
    hint: string,
    order: number,
    title: string,
    answerRequired: boolean
  ) {
    this.id = id;
    this.hint = hint;
    this.order = order;
    this.title = title;
    this.answerRequired = answerRequired;
  }
}

export class TextQuestion extends Question {
  type: string;
  maxLength: ITextQuestionView['maxLength'];
  inputType: ITextQuestionView['inputType'];

  constructor(
    id: number,
    hint: string,
    order: number,
    title: string,
    answerRequired: boolean
  ) {
    super(id, hint, order, title, answerRequired);
    this.type = QuestionType.Text;
    this.maxLength = 0;
    this.inputType = 'text';
  }
}

export class FileQuestion extends Question {
  type: string;

  constructor(
    id: number,
    hint: string,
    order: number,
    title: string,
    answerRequired: boolean
  ) {
    super(id, hint, order, title, answerRequired);
    this.type = QuestionType.File;
  }
}

export class MultipleAnswerQuestion extends Question {
  type: string;
  questionFields: IMultipleAnswerQuestionView['questionFields'];

  constructor(
    id: number,
    hint: string,
    order: number,
    title: string,
    answerRequired: boolean
  ) {
    super(id, hint, order, title, answerRequired);
    this.questionFields = [];
    this.type = QuestionType.MultipleAnswer;
  }
}

export class MultipleChoiceQuestion extends Question {
  type: string;
  viewMode: IMultipleChoiceQuestionView['viewMode'];
  choiceList: IMultipleChoiceQuestionView['choiceList'];
  distinctive: boolean;
  unifyValueAndText: boolean;

  constructor(
    id: number,
    hint: string,
    order: number,
    title: string,
    answerRequired: boolean
  ) {
    super(id, hint, order, title, answerRequired);
    this.type = QuestionType.MultipleChoice;
    this.choiceList = [];
    this.distinctive = false;
    this.viewMode = 'list';
    this.unifyValueAndText = false;
  }
}

export class GridQuestion extends MultipleChoiceQuestion {
  type: string;
  parts: IGridQuestionView['parts'];
  mergeSumAndCount: boolean;

  constructor(
    id: number,
    hint: string,
    order: number,
    title: string,
    answerRequired: boolean
  ) {
    super(id, hint, order, title, answerRequired);
    this.type = QuestionType.Grid;
    this.parts = [];
    this.mergeSumAndCount = false;
  }
}

export class IndicatorQuestion extends Question {
  type: string;
  // unit: string;
  constructor(
    id: number,
    hint: string,
    order: number,
    title: string,
    answerRequired: boolean
  ) {
    super(id, hint, order, title, answerRequired);
    this.type = QuestionType.Indicator;
  }
}

export function getTimeScale(p: PeriodType) {
  if (p === 'none') return null;
  return p as TimeScale;
}

export interface ISurveyReportLink {
  surveyReportToken: string;
  reportId: number;
  itemId: NumberOrNull;
}

export interface IProviderSurveyReport extends IProviderItemView {
  name?: StringOrNull;
  ownerToken?: string;
  reportIds?: number[];
  reportToken?: string;
  selfReportToken?: string;
  changeDataToken?: string;
  surveyReportLinks: ISurveyReportLink[] | null;
  taskReportList: ITaskReportInfo[] | null;
  defaultViewType?: ReportViewType;
  groupByTags?: number[];
  mandatorySearchTags?: number[];
  filterOptions: {
    filterSelectTagRoots?: number[];
    filterTitle?: string;
  }[];
  defaultEndTime?: EndTime;
  forceFilter?: string;
  type: 'surveyReport';
}

export interface ISurveyVersionInfo {
  versionId?: string;
  versionTitle?: string;
  versionDescription?: string;
  creationDate?: number;
  editor?: number;
}
export type SurveyType = 'Regular' | 'Exam' | 'Bank';
export interface ISurveyPartialProperties {
  surveyToken?: string; // editToken Or readToken. it set on requiring survey from server
  uniquePerInstance: boolean;
  uniquePerTime: boolean;
  uniquePerUser: boolean;
  randomizeOrder: boolean;
  fillDurationInMinutes?: number;
  autoSelectRandomQuestions?: number;
  keepSubmitQuestionList: boolean;
  uniquePerTags: boolean;
  keepResponsesAnonymous: boolean;
  keepLatestInstanceIfAnonymous: boolean;
  stageQuestion?: number;
  description: string;
  fields: Array<Field>;
  name: string;
  periodType: PeriodType;
  recommendRefill: boolean;
  scoreMethod?: ScoreMethodType;
  trackResponseChanges?: boolean;
  keepUnique: boolean;
  askTitle: boolean;
  offerToFillAtFirst: boolean;
  sensitiveChangeRequiresEditor: boolean;
  autoLock: boolean;
  versionInfo?: ISurveyVersionInfo;
  surveyType?: SurveyType;
  questionList?: Array<IQuestionsView>;
}

interface ISurveyTarget {
  date: number;
  base: number;
  target: number;
}

export interface ISurveyProperties extends ISurveyPartialProperties {
  questionMap?: {
    [questionId: number]: IQuestionsView;
  };
  reportIds?: [number];
  lastSortingIndex?: number;
}

export type TPeriodType =
  | 'day'
  | 'week'
  | 'month'
  | 'season'
  | 'twoSeason'
  | 'year';

export interface IScheduledPeriod {
  startDate: number;
  endDate?: number;
  periodType: TPeriodType;
  offsetDays: string[];
  invalidDaysOfWeek: string[];
}

export interface ISurveyNotification {
  medium: NotificationMedium;
  messagePattern?: string;
  userId: number;
  changedQuestionId?: number;
  changedQuestionValue?: string;
  conditionQuestionId?: number;
  conditionQuestionValues?: string[];
  action: SurveyAction;
  slackHookABC?: string;
  groupId?: string;
  schedulePeriod?: IScheduledPeriod;
}
